import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Routes, Route, useParams, Navigate } from 'react-router-dom';
import { MessageSquare, FileText, ArrowLeft, HelpCircle, Search } from 'lucide-react';
import { supabase } from './lib/supabase';
import { AuthForm } from './components/AuthForm';
import { ChatPage } from './pages/ChatPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { DocumentViewer } from './pages/DocumentViewer';
import { AccountMenu } from './components/AccountMenu';
import { HelpPage } from './pages/HelpPage';
import { KbAlgoliaSearchPage } from './pages/KbAlgoliaSearchPage';
import type { User } from '@supabase/supabase-js';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (location.pathname === '/logout') {
      handleLogout();
    }
  }, [location.pathname]);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setUser(session?.user ?? null);
    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    navigate('/');
  };

  const handleViewDocument = (documentId: string) => {
    navigate(`/documents/${documentId}`);
  };

  const handleBackToDocuments = () => {
    navigate('/documents');
  };

  function DocumentViewerWrapper({ onBack }: { onBack: () => void }) {
    const { documentId } = useParams<{ documentId: string }>();
    return <DocumentViewer documentId={documentId!} onBack={onBack} />;
  }

  const currentView =
    location.pathname === '/documents/search'
      ? 'kb-search'
      : location.pathname.startsWith('/documents/')
        ? 'document-viewer'
        : location.pathname === '/documents'
          ? 'documents'
          : location.pathname === '/help'
            ? 'help'
            : location.pathname.startsWith('/chat')
              ? 'chat'
              : 'chat';

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-700 dark:border-slate-600 dark:border-t-slate-300" />
      </div>
    );
  }

  if (!user) {
    return <AuthForm onAuthSuccess={checkUser} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <nav className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="flex h-16 w-full items-center justify-between gap-4 px-4 sm:px-6">
          {currentView === 'document-viewer' ? (
            <>
              <button
                type="button"
                onClick={handleBackToDocuments}
                className="flex min-w-0 shrink items-center gap-2 text-slate-600 transition-colors hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100"
              >
                <ArrowLeft className="h-5 w-5 shrink-0" />
                <span className="font-medium">Back to Knowledge Base</span>
              </button>
              <AccountMenu user={user} onLogout={() => void handleLogout()} compactEmail />
            </>
          ) : (
            <>
              <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                <h1 className="shrink-0 text-xl font-bold text-slate-800 dark:text-slate-100">SOPerator AI</h1>
                <div className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('/chat')}
                    className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors sm:px-4 sm:text-base ${
                      currentView === 'chat'
                        ? 'bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                  >
                    <MessageSquare className="h-4 w-4 shrink-0" />
                    Chat
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/documents')}
                    className={`flex min-w-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors sm:px-4 sm:text-base ${
                      currentView === 'documents'
                        ? 'bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                  >
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="truncate">Knowledge Base</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/documents/search')}
                    className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors sm:px-4 sm:text-base ${
                      currentView === 'kb-search'
                        ? 'bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Search className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">Search</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/help')}
                    className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors sm:px-4 sm:text-base ${
                      currentView === 'help'
                        ? 'bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                  >
                    <HelpCircle className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">Help</span>
                  </button>
                </div>
              </div>
              <AccountMenu user={user} onLogout={() => void handleLogout()} />
            </>
          )}
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<Navigate to="/chat" replace />} />
        <Route path="/logout" element={<Navigate to="/" replace />} />
        <Route
          path="/chat"
          element={
            <div className="h-[calc(100vh-4rem)]">
              <ChatPage user={user} />
            </div>
          }
        />
        <Route
          path="/chat/:chatId"
          element={
            <div className="h-[calc(100vh-4rem)]">
              <ChatPage user={user} />
            </div>
          }
        />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/documents/search" element={<KbAlgoliaSearchPage />} />
        <Route
          path="/documents"
          element={<DocumentsPage user={user} onViewDocument={handleViewDocument} />}
        />
        <Route
          path="/documents/:documentId"
          element={<DocumentViewerWrapper onBack={handleBackToDocuments} />}
        />
      </Routes>
    </div>
  );
}

export default App;
